import type { User } from "@/types/fleet";
import { ApiClientError, apiRequest } from "@/lib/api/http";

interface RemoteAuthUser {
  id?: string;
  title?: string;
  username?: string;
  email?: string;
  client_id?: string;
  /** `"1"` / `"0"`. See the note on `normalizeUser` — not shipped upstream yet. */
  is_admin?: string;
}

function normalizeUser(raw: RemoteAuthUser): User {
  const displayName = raw.title?.trim() || raw.username?.trim() || raw.email?.trim() || "User";
  return {
    id: raw.id ?? "0",
    email: raw.email ?? "",
    name: displayName,
    username: raw.username?.trim() ?? "",
    clientId: raw.client_id?.trim() ?? "",
    // `Me.php` carries no role today (`docs/ACTIVITY_API.md` specs `is_admin`), so an absent
    // field means "upstream has not shipped it" rather than "not an admin" — treating it as the
    // latter would hide the activity page from everyone the day this lands. Only an explicit
    // "0" demotes. Deliberately permissive; tighten it once the field is live.
    role: raw.is_admin === "0" ? "viewer" : "admin",
    lastLogin: new Date().toISOString(),
  };
}

interface LoginResponse {
  success: boolean;
  user: RemoteAuthUser | null;
  token: string;
}

export type AuthSession = {
  user: User;
  token: string;
};

export async function loginRequest(username: string, password: string): Promise<AuthSession> {
  const response = await apiRequest<LoginResponse>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });

  if (typeof response.token !== "string" || !response.token) {
    throw new ApiClientError("invalid_response", "Missing token in login response", 502);
  }

  return {
    user: normalizeUser(response.user ?? {}),
    token: response.token,
  };
}

export async function meRequest(): Promise<AuthSession> {
  const response = await apiRequest<Record<string, unknown>>("/api/auth/me", {
    method: "GET",
    cache: "no-store",
  });

  const { token: rawToken, ...userFields } = response;
  const token = typeof rawToken === "string" ? rawToken : "";
  if (!token) {
    throw new ApiClientError("invalid_response", "Missing token in session response", 502);
  }

  return {
    user: normalizeUser(userFields as RemoteAuthUser),
    token,
  };
}

export async function logoutRequest(): Promise<void> {
  await apiRequest<{ success: boolean }>("/api/auth/logout", {
    method: "POST",
  });
}

